import { useEffect, useMemo, useRef, useState } from "react";
import { getCodeViolations } from "../lib/api";

/**
 * V2.0.30 — Shared hook for fetching code-violation data with a
 * debounced re-fetch when the blueprint mutates.
 *
 * Used by:
 *  - LiveInspectorRail (compliance traffic-light)
 *  - FloorPlan2D (per-wall coral-red violation overlay)
 *
 * Lifting this into Editor.jsx via a hook keeps the two consumers in
 * sync without firing two separate /api/designs/{id}/code-violations
 * requests per blueprint mutation.
 */
export default function useCodeViolations(designId, blueprint) {
  const [violationData, setViolationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Stable shallow signature — re-fetches only when room count or area
  // changes (an architectural mutation), NOT on every selection tick.
  const blueprintKey = useMemo(() => {
    if (!blueprint?.floors) return "";
    const parts = [];
    let totalArea = 0;
    for (const f of blueprint.floors) {
      const rs = (f.rooms || []).filter((r) => r?.deleted !== true);
      parts.push(`${f.floor_index || "?"}:${rs.length}`);
      for (const r of rs) {
        totalArea += (Number(r.width) || 0) * (Number(r.depth) || 0);
      }
    }
    return `${parts.join("|")}|${totalArea.toFixed(1)}`;
  }, [blueprint]);

  useEffect(() => {
    if (!designId || !blueprintKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let cancelled = false;
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      getCodeViolations(designId)
        .then((r) => {
          if (!cancelled) setViolationData(r.data);
        })
        .catch(() => {
          if (!cancelled) setViolationData(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 1200);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [designId, blueprintKey]);

  return { violationData, loading };
}
